import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { 
  Select, SelectContent, SelectItem, 
  SelectTrigger, SelectValue 
} from "../../components/ui/select";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle 
} from "../../components/ui/dialog";
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "../../components/ui/alert-dialog";
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from "../../components/ui/table";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  Users, Stethoscope, FlaskConical, Building2, 
  LogOut, Activity, RefreshCw, Clock, Pencil, Trash2,
  MoreHorizontal, Upload, BarChart3, Calendar
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
  >
    <Card className="border-zinc-800 hover:border-zinc-700 transition-colors" style={{ backgroundColor: 'rgba(24, 24, 27, 0.5)', backdropFilter: 'blur(10px)' }}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-400 font-medium">{title}</p>
            <p className="text-3xl font-bold text-white mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const userTypeColors = {
  DOCTOR: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  PATIENT: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  RESEARCHER: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  CLINIC_HOSPITAL: "bg-violet-500/20 text-violet-400 border-violet-500/30"
};

const userTypeLabels = {
  DOCTOR: "Doctor",
  PATIENT: "Patient",
  RESEARCHER: "Researcher",
  CLINIC_HOSPITAL: "Clinic/Hospital"
};

const EventRow = ({ event, isNew, onEdit, onDelete, secretMode }) => {
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("en-IN", { 
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <motion.tr
      initial={isNew ? { backgroundColor: "rgba(139, 92, 246, 0.2)" } : {}}
      animate={{ backgroundColor: "transparent" }}
      transition={{ duration: 2 }}
      className="hover:bg-zinc-900/30 border-b border-zinc-800/50"
      data-testid={`event-row-${event.auth_id}`}
    >
      <TableCell className="py-3 px-4">
        <div className="font-medium text-white">{event.name}</div>
      </TableCell>
      <TableCell className="py-3 px-4">
        <span className="font-mono text-sm text-zinc-300">{event.email}</span>
      </TableCell>
      <TableCell className="py-3 px-4">
        <Badge variant="outline" className={userTypeColors[event.user_type]}>
          {userTypeLabels[event.user_type]}
        </Badge>
      </TableCell>
      <TableCell className="py-3 px-4">
        <span className="text-zinc-300">{event.auth_provider}</span>
      </TableCell>
      <TableCell className="py-3 px-4">
        <span className="font-mono text-sm text-zinc-400">{formatDate(event.auth_time)}</span>
      </TableCell>
      <TableCell className="py-3 px-4">
        <Badge variant={event.is_first_login ? "default" : "secondary"} 
               className={event.is_first_login ? "bg-violet-600" : "bg-zinc-700"}>
          {event.is_first_login ? "Yes" : "No"}
        </Badge>
      </TableCell>
      <TableCell className="py-3 px-4">
        <Badge 
          variant="outline"
          className={event.status === "Active" 
            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" 
            : "bg-red-500/20 text-red-400 border-red-500/30"
          }
        >
          {event.status}
        </Badge>
      </TableCell>
      {secretMode && (
        <TableCell className="py-3 px-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
              <DropdownMenuItem onClick={() => onEdit(event)} className="cursor-pointer hover:bg-zinc-800">
                <Pencil className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(event)} className="cursor-pointer text-red-400 hover:bg-zinc-800">
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      )}
    </motion.tr>
  );
};

const AddEditDialog = ({ open, onOpenChange, event, onSave, mode }) => {
  const [formData, setFormData] = useState({
    name: "", email: "", user_type: "DOCTOR", auth_provider: "Google", status: "Active", is_first_login: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event && mode === "edit") {
      setFormData({
        name: event.name || "", email: event.email || "", user_type: event.user_type || "DOCTOR",
        auth_provider: event.auth_provider || "Google", status: event.status || "Active",
        is_first_login: event.is_first_login ?? true
      });
    } else {
      setFormData({ name: "", email: "", user_type: "DOCTOR", auth_provider: "Google", status: "Active", is_first_login: true });
    }
  }, [event, mode, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Edit User" : "Add New User"}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {mode === "edit" ? "Update user details" : "Add a new user record"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-950 border-zinc-800" required />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-zinc-950 border-zinc-800" required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>User Type</Label>
              <Select value={formData.user_type} onValueChange={(v) => setFormData({ ...formData, user_type: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="DOCTOR">Doctor</SelectItem>
                  <SelectItem value="PATIENT">Patient</SelectItem>
                  <SelectItem value="RESEARCHER">Researcher</SelectItem>
                  <SelectItem value="CLINIC_HOSPITAL">Clinic/Hospital</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Auth Provider</Label>
              <Select value={formData.auth_provider} onValueChange={(v) => setFormData({ ...formData, auth_provider: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="Google">Google</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="OTP">OTP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>First Login</Label>
              <Select value={formData.is_first_login ? "yes" : "no"} onValueChange={(v) => setFormData({ ...formData, is_first_login: v === "yes" })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">Cancel</Button>
            <Button type="submit" className="bg-violet-600 hover:bg-violet-700" disabled={saving}>
              {saving ? "Saving..." : mode === "edit" ? "Update" : "Add User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CSVUploadDialog = ({ open, onOpenChange, onUpload }) => {
  const [csvData, setCsvData] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setCsvData(event.target.result);
      reader.readAsText(file);
    }
  };

  const handleSubmit = async () => {
    setUploading(true);
    const lines = csvData.trim().split('\n');
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const items = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const item = {};
      headers.forEach((h, idx) => { item[h] = values[idx] || ''; });
      if (item.name && item.email) {
        items.push({
          name: item.name,
          email: item.email,
          user_type: item.user_type || item.type || 'patient',
          auth_provider: item.auth_provider || item.provider || 'Email'
        });
      }
    }
    
    await onUpload(items);
    setUploading(false);
    setCsvData("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>Upload CSV</DialogTitle>
          <DialogDescription className="text-zinc-400">
            CSV format: name, email, user_type, auth_provider
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input type="file" accept=".csv" onChange={handleFileUpload} className="bg-zinc-950 border-zinc-800" />
          <textarea 
            value={csvData} 
            onChange={(e) => setCsvData(e.target.value)}
            placeholder="Or paste CSV data here..."
            className="w-full h-32 p-3 bg-zinc-950 border border-zinc-800 rounded-md text-sm font-mono text-white"
          />
          <p className="text-xs text-zinc-500">Example: John Doe, john@example.com, doctor, Google</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">Cancel</Button>
          <Button onClick={handleSubmit} className="bg-violet-600 hover:bg-violet-700" disabled={uploading || !csvData}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminDashboardPage = ({ token, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, doctors: 0, patients: 0, researchers: 0, clinic_hospital: 0 });
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [newEventIds, setNewEventIds] = useState(new Set());
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  const [secretMode, setSecretMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Secret key combo: Ctrl+Shift+A
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setSecretMode(prev => !prev);
        toast.info(secretMode ? "Admin mode disabled" : "Admin mode enabled");
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [secretMode]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth-events/stats`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, [token]);

  const fetchEvents = useCallback(async () => {
    try {
      const params = activeTab !== "all" ? `?user_type=${activeTab}` : "";
      const res = await fetch(`${API}/auth-events${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        
        const currentIds = new Set(events.map(e => e.auth_id));
        const newIds = data.filter(e => !currentIds.has(e.auth_id)).map(e => e.auth_id);
        
        if (newIds.length > 0) {
          setNewEventIds(new Set(newIds));
          setTimeout(() => setNewEventIds(new Set()), 3000);
        }
        
        setEvents(data);
        setLastUpdate(new Date());
      }
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, events]);

  useEffect(() => {
    fetchStats();
    fetchEvents();
    const interval = setInterval(() => { fetchStats(); fetchEvents(); }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [activeTab]);

  const handleRefresh = () => { setLoading(true); fetchStats(); fetchEvents(); };
  const handleEditUser = (event) => { setSelectedEvent(event); setDialogMode("edit"); setDialogOpen(true); };
  const handleDeleteUser = (event) => { setEventToDelete(event); setDeleteDialogOpen(true); };
  const handleAddUser = () => { setSelectedEvent(null); setDialogMode("add"); setDialogOpen(true); };

  const handleSaveUser = async (formData) => {
    try {
      if (dialogMode === "add") {
        const res = await fetch(`${API}/auth-events/manual`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (res.ok) {
          toast.success("User added successfully");
        } else {
          throw new Error("Failed to add user");
        }
      } else {
        const res = await fetch(`${API}/auth-events/${selectedEvent.auth_id}`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (res.ok) {
          toast.success("User updated successfully");
        } else {
          throw new Error("Failed to update user");
        }
      }
      setDialogOpen(false);
      fetchStats(); fetchEvents();
    } catch (err) {
      toast.error(err.message || "Failed to save user");
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${API}/auth-events/${eventToDelete.auth_id}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success("User deleted successfully");
        setDeleteDialogOpen(false);
        fetchStats(); fetchEvents();
      } else {
        throw new Error("Failed to delete");
      }
    } catch (err) {
      toast.error("Failed to delete user");
    }
  };

  const handleCSVUpload = async (items) => {
    try {
      const res = await fetch(`${API}/auth-events/bulk-upload`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(items)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.created} users`);
        if (data.errors && data.errors.length > 0) {
          toast.warning(`${data.errors.length} errors occurred`);
        }
        fetchStats(); fetchEvents();
      }
    } catch (err) {
      toast.error("Failed to upload CSV");
    }
  };

  const filteredEvents = activeTab === "all" ? events : events.filter(e => e.user_type === activeTab);

  return (
    <div className="min-h-screen" style={{ 
      background: '#09090b',
      backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)`,
      backgroundSize: '40px 40px'
    }}>
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-violet-800 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>MedPlexity Admin</h1>
              <p className="text-xs text-zinc-500">Authentication Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Clock className="w-3 h-3" />
              <span>Updated: {lastUpdate.toLocaleTimeString()}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/bookings')} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              <Calendar className="w-4 h-4 mr-2" /> Bookings
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/stats')} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              <BarChart3 className="w-4 h-4 mr-2" /> Stats
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard title="Total Signups" value={stats.total} icon={Users} color="bg-violet-600" delay={0} />
          <StatCard title="Doctors" value={stats.doctors} icon={Stethoscope} color="bg-emerald-600" delay={0.1} />
          <StatCard title="Patients" value={stats.patients} icon={Users} color="bg-blue-600" delay={0.2} />
          <StatCard title="Researchers" value={stats.researchers} icon={FlaskConical} color="bg-amber-600" delay={0.3} />
          <StatCard title="Clinics/Hospitals" value={stats.clinic_hospital} icon={Building2} color="bg-rose-600" delay={0.4} />
        </div>

        <Card className="border-zinc-800" style={{ backgroundColor: 'rgba(24, 24, 27, 0.5)', backdropFilter: 'blur(10px)' }}>
          <CardHeader className="border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Authentication Events</CardTitle>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                  {filteredEvents.length} records
                </Badge>
                {secretMode && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setCsvDialogOpen(true)} className="border-zinc-700">
                      <Upload className="w-4 h-4 mr-2" /> CSV
                    </Button>
                    <Button size="sm" onClick={handleAddUser} className="bg-violet-600 hover:bg-violet-700">
                      + Add
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="border-b border-zinc-800 px-6 pt-4">
                <TabsList className="bg-zinc-900/50 border border-zinc-800">
                  <TabsTrigger value="all" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">All</TabsTrigger>
                  <TabsTrigger value="DOCTOR" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Doctors</TabsTrigger>
                  <TabsTrigger value="PATIENT" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">Patients</TabsTrigger>
                  <TabsTrigger value="RESEARCHER" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Researchers</TabsTrigger>
                  <TabsTrigger value="CLINIC_HOSPITAL" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white">Clinics/Hospitals</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value={activeTab} className="mt-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-900/50 hover:bg-zinc-900/50 border-b border-zinc-800">
                        <TableHead className="text-zinc-400 font-medium uppercase tracking-wider text-xs py-3 px-4">Name</TableHead>
                        <TableHead className="text-zinc-400 font-medium uppercase tracking-wider text-xs py-3 px-4">Email ID</TableHead>
                        <TableHead className="text-zinc-400 font-medium uppercase tracking-wider text-xs py-3 px-4">User Type</TableHead>
                        <TableHead className="text-zinc-400 font-medium uppercase tracking-wider text-xs py-3 px-4">Auth Provider</TableHead>
                        <TableHead className="text-zinc-400 font-medium uppercase tracking-wider text-xs py-3 px-4">Time (IST)</TableHead>
                        <TableHead className="text-zinc-400 font-medium uppercase tracking-wider text-xs py-3 px-4">First Login</TableHead>
                        <TableHead className="text-zinc-400 font-medium uppercase tracking-wider text-xs py-3 px-4">Status</TableHead>
                        {secretMode && <TableHead className="text-zinc-400 font-medium uppercase tracking-wider text-xs py-3 px-4">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <AnimatePresence>
                        {filteredEvents.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={secretMode ? 8 : 7} className="text-center py-12 text-zinc-500">
                              {loading ? (
                                <div className="flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-violet-500"></div>
                                  <span>Loading...</span>
                                </div>
                              ) : <p>No events found</p>}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredEvents.map((event) => (
                            <EventRow key={event.auth_id} event={event} isNew={newEventIds.has(event.auth_id)}
                              onEdit={handleEditUser} onDelete={handleDeleteUser} secretMode={secretMode} />
                          ))
                        )}
                      </AnimatePresence>
                    </TableBody>
                  </Table>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {secretMode && <p className="text-center text-xs text-zinc-600 mt-4">Admin Mode Active • Ctrl+Shift+A to toggle</p>}
      </main>

      <AddEditDialog open={dialogOpen} onOpenChange={setDialogOpen} event={selectedEvent} onSave={handleSaveUser} mode={dialogMode} />
      <CSVUploadDialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen} onUpload={handleCSVUpload} />
      
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Delete <span className="text-white font-semibold">{eventToDelete?.name}</span>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboardPage;
